import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, Table, Button, Space, Input, Select, Switch, App, Tooltip, Badge } from 'antd';
import { ReloadOutlined, SearchOutlined, ThunderboltOutlined, RightOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../store/AuthContext';
import CheckerStatsLine, { CheckerStats } from '../components/CheckerStatsLine';
import { HealthServer } from '../components/healthMonitorTypes';
import { HistoryCell, HistoryHeader, MainIPCell, NameCell, TagsCell } from '../components/healthMonitorCells';
import { NetworkChips, ProxyChips } from '../components/healthMonitorChips';

const { Title, Text } = Typography;

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '--';
  const diff = Date.now() - new Date(dateStr).getTime();
  const absSecs = Math.max(0, Math.floor(diff / 1000));
  if (absSecs < 60) return `${absSecs}s ago`;
  const mins = Math.floor(absSecs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const DashboardPage: React.FC = () => {
  const { message } = App.useApp();
  const { user } = useAuth();
  const [servers, setServers] = useState<HealthServer[]>([]);
  const [total, setTotal] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [sseConnected, setSseConnected] = useState(false);
  const [checkingAll, setCheckingAll] = useState(false);
  const [checkingKeys, setCheckingKeys] = useState<Set<string>>(new Set());
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const sseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchInFlightRef = useRef(false);

  const [checkerStats, setCheckerStats] = useState<CheckerStats | null>(null);
  const [countryOptions, setCountryOptions] = useState<{ label: string; value: string }[]>([]);
  const [allTagOptions, setAllTagOptions] = useState<{ label: string; value: string }[]>([]);

  const canWrite = user?.role === 'admin' || user?.role === 'operator';

  const fetchCheckerStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/health/checker-stats');
      if (data.data) setCheckerStats(data.data);
    } catch {
      // silently ignore — checker may be unavailable
    }
  }, []);

  const fetchFilters = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/servers/filters');
      if (data.data) {
        setCountryOptions((data.data.countries || []).map((c: string) => ({ label: c, value: c })));
        setAllTagOptions((data.data.tags || []).map((t: string) => ({ label: t, value: t })));
      }
    } catch {
      // silently ignore
    }
  }, []);

  const fetchServers = useCallback(async (showLoading = false) => {
    // Prevent concurrent fetches
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    if (showLoading) setUserLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: pageSize };
      if (search) params.search = search;
      if (countryFilter) params.country = countryFilter;
      if (statusFilter) params.status = statusFilter;
      if (modeFilter) params.checkMode = modeFilter;
      if (typeFilter) params.type = typeFilter;
      if (tagFilter.length > 0) params.tag = tagFilter.join(',');

      const { data } = await apiClient.get('/health/servers', { params });
      setServers(data.data.items || []);
      setTotal(data.data.total || 0);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load health data');
    } finally {
      fetchInFlightRef.current = false;
      setInitialLoading(false);
      setUserLoading(false);
    }
  }, [page, pageSize, search, countryFilter, statusFilter, modeFilter, typeFilter, tagFilter, message]);

  // Debounced refresh: collects SSE events, fetches at most once per 5s
  const debouncedRefresh = useCallback(() => {
    if (sseDebounceRef.current) return; // already scheduled
    sseDebounceRef.current = setTimeout(() => {
      sseDebounceRef.current = null;
      fetchServers();
      fetchCheckerStats();
    }, 5000);
  }, [fetchServers, fetchCheckerStats]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  // Show loading on filter/pagination/initial changes (fetchServers identity changes)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchServers(); // initial load — no spinner, initialLoading handles it
    } else {
      fetchServers(true); // user changed filters/pagination — show spinner
    }
    fetchCheckerStats();
  }, [fetchServers, fetchCheckerStats]);

  useEffect(() => {
    if (!autoRefresh) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setSseConnected(false);
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (sseDebounceRef.current) { clearTimeout(sseDebounceRef.current); sseDebounceRef.current = null; }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      intervalRef.current = setInterval(fetchServers, 30000);
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }

    const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
    const es = new EventSource(`${baseUrl}/health/stream?token=${token}`);
    eventSourceRef.current = es;

    es.addEventListener('health', () => {
      debouncedRefresh();
    });

    es.onopen = () => {
      setSseConnected(true);
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };

    es.onerror = () => {
      setSseConnected(false);
      es.close();
      eventSourceRef.current = null;
      // Fallback to polling
      if (!intervalRef.current) {
        intervalRef.current = setInterval(fetchServers, 30000);
      }
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
      setSseConnected(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (sseDebounceRef.current) { clearTimeout(sseDebounceRef.current); sseDebounceRef.current = null; }
    };
  }, [autoRefresh, fetchServers, debouncedRefresh]);

  const startCooldown = (seconds: number) => {
    setCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCheckAll = async () => {
    if (cooldown > 0) return;
    setCheckingAll(true);
    try {
      await apiClient.post('/health/check', {});
      message.success('Health checks triggered');
      startCooldown(30);
      setTimeout(fetchServers, 3000);
    } catch (err: any) {
      if (err.response?.status === 429) {
        message.warning(err.response.data?.error || 'Please wait before triggering another check');
        startCooldown(30);
      } else {
        message.error(err.response?.data?.error || 'Failed to trigger checks');
      }
    } finally {
      setCheckingAll(false);
    }
  };

  const handleCheckOne = async (key: string) => {
    if (cooldown > 0) return;
    setCheckingKeys((prev) => new Set(prev).add(key));
    try {
      await apiClient.post('/health/check', { serverKeys: [key] });
      message.success('Check triggered');
      startCooldown(15);
      setTimeout(fetchServers, 3000);
    } catch (err: any) {
      if (err.response?.status === 429) {
        message.warning(err.response.data?.error || 'Please wait before re-checking');
        startCooldown(15);
      } else {
        message.error(err.response?.data?.error || 'Failed to trigger check');
      }
    } finally {
      setCheckingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Column widths are budgeted against 1072px — the content width with the
  // navigation rail EXPANDED, which is the tighter of the two states. If it
  // fits there it fits collapsed; the reverse is not true.
  //
  // Every column is measured against its own header and its own data, because
  // the previous budget was not: "Checked" had 66px for a 7-character title and
  // broke it across two lines as "Checke / d", which alone made the header row
  // twice as tall as it needed to be; meanwhile Server was the only column
  // without a width, so it absorbed ALL the slack and sat at ~370px to display
  // "stand-dup". Server is now pinned and the chip column — the one with real
  // variable-length content, and the point of this screen — is the one that
  // takes the slack.
  //
  //   Server 170 + IP 136 + Networks 160 + History 124 + Checked 76
  //   + Tags 72 + Actions 76 = 814 fixed, leaving 258px for Proxies/Modems
  //   at 1072 (two "SOCKS5 :1080 DOWN" chips per line) and 386px at 1200.
  //   No horizontal scroll in either state. Networks grew: its chips are
  //   "<ip> <STATE>" and at 144px the STATE word was
  //   the part being clipped. Server shrank because the flag
  //   and type icon moved to the trailing edge and the status dot moved in.
  const columns = [
    {
      title: 'Server',
      dataIndex: 'name',
      key: 'name',
      width: 170,
      render: (_: string, record: HealthServer) => <NameCell record={record} />,
    },
    {
      title: 'Main IP',
      dataIndex: 'mainIP',
      key: 'mainIP',
      width: 136,
      render: (_: string, record: HealthServer) => <MainIPCell record={record} />,
    },
    {
      title: 'Networks',
      key: 'networkStatuses',
      width: 160,
      render: (_: any, record: HealthServer) => <NetworkChips record={record} />,
    },
    {
      // The only column left to flex: chips carry full host:port + state, so
      // every pixel of slack here removes a wrapped line from a row.
      title: 'Proxies / Modems',
      key: 'proxyStatuses',
      render: (_: any, record: HealthServer) => <ProxyChips record={record} />,
    },
    {
      title: <HistoryHeader />,
      key: 'history',
      width: 124,
      render: (_: any, record: HealthServer) => <HistoryCell record={record} />,
    },
    {
      // Right-aligned: these are all "<number><unit> ago", and right alignment
      // puts the magnitude in a fixed place so a stale row stands out from a
      // column of fresh ones without having to be read.
      title: 'Checked',
      dataIndex: 'lastCheck',
      key: 'lastCheck',
      width: 76,
      align: 'right' as const,
      render: (v: string | null) => <Text type="secondary" style={{ fontSize: 12 }}>{timeAgo(v)}</Text>,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 72,
      render: (tags: string[]) => <TagsCell tags={tags} />,
    },
    {
      // This column used to have no header at all, so two icon buttons sat
      // under a blank heading with nothing saying they were controls.
      title: 'Actions',
      key: 'actions',
      width: canWrite ? 76 : 48,
      align: 'center' as const,
      render: (_: any, record: HealthServer) => (
        <Space size={4}>
          {canWrite && (
            <Tooltip title={cooldown > 0 ? `Cooling down — ${cooldown}s` : 'Run check now'}>
              <span>
                <Button
                  size="small"
                  aria-label={`Run health check now for ${record.name}`}
                  icon={<ThunderboltOutlined />}
                  loading={checkingKeys.has(record.key)}
                  disabled={cooldown > 0}
                  onClick={() => handleCheckOne(record.key)}
                />
              </span>
            </Tooltip>
          )}
          <Tooltip title="Open health details">
            <Link to={`/servers/${record.key}/health`} aria-label={`Open health details for ${record.name}`}>
              <Button size="small" icon={<RightOutlined />} />
            </Link>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 8,
      }}>
        <Space size={12} align="center" wrap>
          <Title level={4} style={{ margin: 0 }}>Health Monitor</Title>
          {checkerStats && <CheckerStatsLine stats={checkerStats} total={total} />}
        </Space>
        <Space size={8} align="center" wrap>
          {autoRefresh && (
            <Tooltip title={sseConnected ? 'Live updates connected' : 'Polling fallback'}>
              <Badge status={sseConnected ? 'success' : 'warning'} />
            </Tooltip>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>Auto-refresh</Text>
          <Switch checked={autoRefresh} onChange={setAutoRefresh} size="small" />
          <Button size="small" icon={<ReloadOutlined />} onClick={() => { fetchServers(true); fetchCheckerStats(); }}>
            Refresh
          </Button>
          {canWrite && (
            <Button size="small" type="primary" onClick={handleCheckAll} loading={checkingAll} disabled={cooldown > 0}>
              {cooldown > 0 ? `Check All (${cooldown}s)` : 'Check All'}
            </Button>
          )}
        </Space>
      </div>

      <Space size={8} style={{ marginBottom: 8, rowGap: 8 }} wrap>
        <Input.Search
          size="small"
          placeholder="Name, IP, host:port, modem…"
          allowClear
          prefix={<SearchOutlined />}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          style={{ width: 200 }}
        />
        <Select
          size="small"
          placeholder="Country"
          allowClear
          showSearch
          style={{ width: 96 }}
          onChange={(v) => { setCountryFilter(v || ''); setPage(1); }}
          options={countryOptions}
        />
        <Select
          size="small"
          placeholder="Status"
          allowClear
          style={{ width: 104 }}
          onChange={(v) => { setStatusFilter(v || ''); setPage(1); }}
          options={[
            { label: 'HEALTHY', value: 'HEALTHY' },
            { label: 'DEGRADED', value: 'DEGRADED' },
            { label: 'DOWN', value: 'DOWN' },
            { label: 'ERROR', value: 'ERROR' },
            { label: 'ZOMBIE', value: 'ZOMBIE' },
            { label: 'SUSPECT', value: 'SUSPECT' },
            { label: 'UNKNOWN', value: 'UNKNOWN' },
          ]}
        />
        <Select
          size="small"
          placeholder="Mode"
          allowClear
          style={{ width: 110 }}
          onChange={(v) => { setModeFilter(v || ''); setPage(1); }}
          options={[
            { label: 'Active Probing', value: 'active-probing' },
            { label: 'Agent Heartbeat', value: 'agent-heartbeat' },
          ]}
        />
        <Select
          size="small"
          placeholder="Type"
          allowClear
          style={{ width: 118 }}
          onChange={(v) => { setTypeFilter(v || ''); setPage(1); }}
          options={[
            { label: 'Dedicated', value: 'dedicated' },
            { label: 'Mobile gateway', value: 'mobile-gateway' },
          ]}
        />
        <Select
          size="small"
          mode="multiple"
          placeholder="Tag"
          allowClear
          showSearch
          maxTagCount="responsive"
          style={{ minWidth: 120, maxWidth: 200 }}
          onChange={(v: string[]) => { setTagFilter(v); setPage(1); }}
          value={tagFilter}
          options={allTagOptions}
        />
      </Space>

      <Table
        dataSource={servers}
        columns={columns}
        rowKey="key"
        loading={initialLoading || userLoading}
        size="small"
        tableLayout="fixed"
        pagination={{
          current: page,
          pageSize,
          total,
          size: 'small',
          showSizeChanger: true,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
      />
    </>
  );
};

export default DashboardPage;
